import { languageLocale, t } from './i18n';
import { ApiError } from './api';

export type FeedbackTone = 'error' | 'warning' | 'success' | 'info';

export type FeedbackAction = {
  label: string;
  onClick: () => void;
};

export type FeedbackNoticeState = {
  tone: FeedbackTone;
  title?: string;
  message: string;
  action?: FeedbackAction;
};

export type ActionFeedback = {
  scope: string;
  action: string;
  resourceId?: string;
  title: string;
  message: string;
  code?: string;
  params?: Record<string, unknown>;
  details?: Record<string, unknown>;
};

export function DataError({ error, title }: { error: string; title?: string }) {
  if (!error) return null;
  return <FeedbackNotice tone="error" title={title} message={error} placement="page" />;
}

export function ActionErrorNotice({
  error,
  onDismiss,
}: {
  error: ActionFeedback;
  onDismiss?: () => void;
}) {
  return (
    <FeedbackNotice
      tone="error"
      title={error.title}
      message={error.message}
      onDismiss={onDismiss}
      placement="section"
    />
  );
}

export function FeedbackNotice({
  tone = 'error',
  title,
  message,
  action,
  onDismiss,
  placement = 'section',
  className = '',
}: FeedbackNoticeState & {
  onDismiss?: () => void;
  placement?: 'page' | 'section' | 'inline';
  className?: string;
}) {
  const cleanMessage = tidyFeedbackMessage(message);
  const icon = tone === 'success' ? '+' : tone === 'info' ? 'i' : tone === 'warning' ? '!' : '!';
  return (
    <div
      className={[
        'inline-alert',
        'feedback-notice',
        `${tone}-alert`,
        `feedback-notice-${tone}`,
        `feedback-notice-${placement}`,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'status'}
    >
      <span className="inline-alert-icon feedback-notice-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="inline-alert-content feedback-notice-content">
        {title ? (
          <strong className="inline-alert-title feedback-notice-title">{title}</strong>
        ) : null}
        <span className="inline-alert-message feedback-notice-message">{cleanMessage}</span>
        {action ? (
          <div className="inline-alert-actions feedback-notice-actions">
            <button className="btn-secondary btn-sm" type="button" onClick={action.onClick}>
              {action.label}
            </button>
          </div>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          className="inline-alert-dismiss feedback-notice-dismiss"
          type="button"
          onClick={onDismiss}
          aria-label={t('feedback.dismiss')}
        >
          <span aria-hidden="true">x</span>
        </button>
      ) : null}
    </div>
  );
}

export function actionFeedbackFromUnknown(
  err: unknown,
  options: Pick<ActionFeedback, 'scope' | 'action' | 'resourceId' | 'title'> & { fallback: string },
): ActionFeedback {
  const message = err instanceof Error ? err.message : options.fallback;
  if (err instanceof ApiError) {
    return {
      scope: options.scope,
      action: options.action,
      resourceId: options.resourceId,
      title: options.title,
      message,
      code: err.code || err.type,
      params: err.params,
      details: err.details,
    };
  }
  return {
    scope: options.scope,
    action: options.action,
    resourceId: options.resourceId,
    title: options.title,
    message,
  };
}

export function actionFeedbackMatches(
  error: ActionFeedback | null,
  scope: string,
  action: string,
  resourceId?: string,
) {
  if (!error || error.scope !== scope || error.action !== action) return false;
  return resourceId === undefined || error.resourceId === resourceId;
}

export function numberParam(params: Record<string, unknown> | undefined, ...keys: string[]) {
  for (const key of keys) {
    const value = params?.[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

export function tidyFeedbackMessage(message: string) {
  const localized = localizeCommonMessage(message);
  return localized.replace(/\$(\d+(?:\.\d+)?)/g, (match, amount: string) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return match;
    return new Intl.NumberFormat(languageLocale(), {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  });
}

function localizeCommonMessage(message: string) {
  const normalized = message.trim().toLowerCase();
  if (normalized === 'unauthorized') return t('feedback.unauthorized_please_sign_in_again');
  if (normalized === 'session expired') return t('feedback.session_expired_please_sign_in_again');
  if (normalized === 'failed to fetch')
    return t('feedback.network_request_failed_check_your_connection');
  if (normalized === 'not found') return t('feedback.not_found');
  const requestFailure = message.match(/^Request failed \((\d+)\)$/i);
  return requestFailure ? t('feedback.request_failed', { code: requestFailure[1] }) : message;
}
